import { ClassModel, action, flow, register, types } from "@gadgetinc/mobx-quick-tree";
import { toaster } from "baseui/toast";
import { compact, isEqual, uniq } from "lodash";
import { FrontendBranding } from "state-trees/src//FrontendBranding";
import { AppTemplateType, WebAppAuthType } from "state-trees/src/AppTemplateType";
import { ShopifyAppType } from "state-trees/src/assistant/AssistantMetadata";
import type { FetchData } from "use-http";
import AppTemplates, { type AppTemplateDescriptor, webAppAuthTypeToAppTemplateType } from "../AppTemplates";
import { Survey } from "./Survey";

export const normalizeSubdomainName = (name: string) => name.toLowerCase().replaceAll(" ", "-");
export const sanitizeSubdomainInput = (name: string) => name.replace(/[^a-zA-Z0-9- ]+/g, "");

@register
export class App extends ClassModel({
  internalName: types.optional(types.string, ""),
  frameworkVersion: types.maybeNull(types.string),
  description: types.optional(types.string, ""),
  branding: types.optional(FrontendBranding, {}),
  shopifyAppType: types.optional(types.enumeration<ShopifyAppType>("ShopifyAppType", Object.values(ShopifyAppType)), ShopifyAppType.Custom),
}) {
  get subdomain() {
    return normalizeSubdomainName(this.internalName);
  }

  @action
  setSubdomain(name: string) {
    this.internalName = sanitizeSubdomainInput(name);
  }

  @action
  setDescription(description: string) {
    this.description = description;
  }

  @action
  setFrameworkVersion(version: string) {
    this.frameworkVersion = version;
  }

  @action
  setShopifyAppType(type: ShopifyAppType) {
    this.shopifyAppType = type;
  }
}

@register
export class NewAppForm extends ClassModel({
  appTemplateType: types.maybe(types.enumeration<AppTemplateType>(Object.values(AppTemplateType))),
  appTemplateDescriptor: types.maybeNull(types.frozen<AppTemplateDescriptor>()),
  forkFromAppId: types.maybe(types.string),
  frontendFramework: types.optional(types.string, "react"),
  withAutoComponents: types.maybe(types.string),
  webAppAuthType: types.optional(types.enumeration<WebAppAuthType>(Object.values(WebAppAuthType)), WebAppAuthType.EmailPassword),
  language: types.optional(types.enumeration(["javascript", "typescript"]), "typescript"),
  submitting: types.optional(types.boolean, false),
  validating: types.optional(types.boolean, false),
  successful: types.optional(types.boolean, false),
  suspended: types.optional(types.boolean, false),
  errors: types.map(types.string),
  step: types.optional(types.number, 0),
  lowestAccessibleStep: types.optional(types.number, 0),
  app: App,
  tags: types.optional(types.array(types.string), []),
  survey: types.maybe(Survey),
}) {
  get submittable() {
    return !!(this.appTemplateType && this.app.subdomain && !this.submitting && this.errors.size == 0);
  }

  get editable() {
    return !this.submitting && !this.successful;
  }

  get surveySteps() {
    return this.survey?.steps ?? [];
  }

  dataBlob() {
    const tags = uniq([
      ...compact(this.surveySteps.filter((step) => step.key != "what-are-you-building").flatMap((step) => Object.values(step.tags()))),
      ...this.tags,
    ]);
    return {
      subdomain: this.app.subdomain,
      forkFromAppId: this.forkFromAppId,
      tags,
      appType:
        this.appTemplateType === AppTemplateType.WebAppWithAuth
          ? webAppAuthTypeToAppTemplateType(this.webAppAuthType)
          : this.appTemplateType,
      webAppAuthType: this.webAppAuthType,
      frameworkVersion: this.app.frameworkVersion,
      frontendFramework: this.frontendFramework,
      withAutoComponents: this.withAutoComponents,
      typescript: this.language == "typescript",
      branding: this.app.branding.dataBlob(),
      description: this.app.description.trim(),
      shopifyAppType: this.appTemplateType === AppTemplateType.ShopifyApp ? this.app.shopifyAppType ?? ShopifyAppType.Public : undefined,
    };
  }

  get conversionValue() {
    const experienceStep = this.surveySteps.find((step) => step.key == "experience");
    const experienceValue = experienceStep?.answers.get("experience");
    switch (experienceValue) {
      case "one_to_three_years":
        return 25;
      case "three_plus_years":
        return 50;
      default:
        return 0;
    }
  }

  @action
  setLowestAccessibleStep(value: number) {
    this.lowestAccessibleStep = value;
  }

  @action
  moveToStep(value: number) {
    this.step = value;
  }

  @action
  moveToNextStep() {
    this.step = this.step + 1;
  }

  @action
  moveToPreviousStep() {
    this.step = this.step - 1;
  }

  @action
  setSuspended(suspended: boolean) {
    this.suspended = suspended;
  }

  @action
  validate = flow(function* (this: NewAppForm, request: FetchData) {
    if (this.validating || this.submitting) return;
    if (!this.app.subdomain) return;
    this.validating = true;
    const validatedData = this.dataBlob();
    const response = yield request(validatedData);
    this.validating = false;

    if (response && response.errors) {
      if (isEqual(validatedData, this.dataBlob())) {
        this.errors.replace(response.errors);
      }
    } else {
      this.errors.clear();
    }
  });

  @action
  setFrontendFramework(value: string) {
    this.frontendFramework = value;
  }

  @action
  setLanguage(value: "javascript" | "typescript") {
    this.language = value;
  }

  @action
  setWithAutoComponents(value: string) {
    this.withAutoComponents = value;
  }

  @action
  setWebAppAuthType(value: WebAppAuthType) {
    this.webAppAuthType = value;
  }

  @action
  setAppTemplate(descriptor: AppTemplateDescriptor | null) {
    if (descriptor) {
      this.appTemplateDescriptor = descriptor;
      this.forkFromAppId = descriptor.applicationId;
      if (descriptor.frontendFramework) {
        this.setFrontendFramework(descriptor.frontendFramework);
      }
      if (descriptor.withAutoComponents) {
        this.setWithAutoComponents(descriptor.withAutoComponents);
      }
    } else {
      this.appTemplateDescriptor = null;
      this.forkFromAppId = undefined;
      this.language = "typescript";
      this.withAutoComponents = undefined;
      this.frontendFramework = "react";
    }
  }

  @action
  addTag(tag: string) {
    this.tags.push(tag);
  }

  @action
  submit = flow(function* (this: NewAppForm, request: FetchData, validate: FetchData) {
    this.submitting = true;
    const response = yield request(this.dataBlob());
    this.submitting = false;

    (window as any).dataLayer?.push({
      event: "App created",
      conversionValue: this.conversionValue,
      appType: this.appTemplateType,
      appSubdomain: this.app.subdomain,
    });

    if (response.editURL) {
      this.successful = true;
      window.location.href = response.editURL;
    } else {
      toaster.negative("There was an error creating your app, please try again.", { autoHideDuration: 4000 });
      yield this.validate(validate);
    }
  });

  @action
  setAppType(value: AppTemplateType) {
    this.appTemplateType = value;
  }

  @action
  setAppTemplateFromAppTemplateType(value?: AppTemplateType | null) {
    const appTemplateDescriptor =
      value && "Base" in AppTemplates[value] && AppTemplates[value].Base.length ? AppTemplates[value].Base[0] : null;
    this.setAppTemplate(appTemplateDescriptor);
  }
}
